/**
 * useDeleteEvent — Delete an event from the unified `events` table along
 * with its `performance_events` junction rows.
 *
 * Order: junction first (no cascade on performance_events.event_id),
 * then events. `rodeo_round_configs.event_id` cascades from `events(id)`
 * so round configs go too.
 *
 * Caches invalidated on success:
 *   - eventsKeys.all              — unified primary
 *   - performanceEventsKeys.all   — unified junction
 *   - jackpotOpsKeys.all          — /jackpot-ops embeds events
 *   - rodeoOpsKeys.all            — rodeo detail / wizard config views
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EventsClient } from '../api/generated_clients/generated-events-api-client';
import { PerformanceEventsClient } from '../api/generated_clients/generated-performance_events-api-client';
import { eventsKeys } from '../api/generated_hooks/generated-events-hooks';
import { performanceEventsKeys } from '../api/generated_hooks/generated-performance_events-hooks';
import { jackpotOpsKeys } from '../api/extensions/useJackpotOps';
import { rodeoOpsKeys } from '../api/extensions/useRodeoOps';

/**
 * Delete an event by id and invalidate every cache that could render it.
 *
 * @returns React Query mutation: `mutate(id)` / `mutateAsync(id)`.
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const eventsClient = new EventsClient();
  const junctionClient = new PerformanceEventsClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // 1) Wipe the performance_events junction.
      const links = (await junctionClient.getAll({
        filters: { event_id: id },
      } as any)) as any;
      const linkRows: Array<{ id?: string }> = Array.isArray(links)
        ? links
        : (links?.data ?? []);
      await Promise.all(
        linkRows
          .map((l) => l?.id)
          .filter((rid): rid is string => typeof rid === 'string')
          .map((rid) => junctionClient.delete(rid)),
      );

      // 2) Delete the events row (cascade-removes round_configs).
      await eventsClient.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.all });
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
      queryClient.invalidateQueries({ queryKey: jackpotOpsKeys.all });
      queryClient.invalidateQueries({ queryKey: rodeoOpsKeys.all });
    },
  });
}
